import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  TextField,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { testProxyHealthCheck } from '../../services/proxyManagerService';
import { PROXY_MANAGER_PERMISSION_RESOURCE } from './constants';

interface HealthCheckButtonProps {
  proxyId?: string;
  address: string;
  /** Prefilled targets — the user can edit them before running the check. */
  defaultTargets?: string[];
  protocol?: string | null;
  onSuccess?: () => void;
}

export const HealthCheckButton = ({
  proxyId,
  address,
  defaultTargets = [],
  protocol,
  onSuccess,
}: HealthCheckButtonProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState<string[]>(defaultTargets);

  const handleOpen = () => {
    setTargets(defaultTargets);
    setOpen(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await testProxyHealthCheck(String(proxyId), {
        targets,
        protocol: protocol || 'HTTP',
      });
      notify(translate('module.proxyManager.healthCheckSuccess'), {
        type: 'success',
        autoHideDuration: 1500,
      });
      onSuccess?.();
      setOpen(false);
    } catch (error: any) {
      notify(
        error?.body?.message ?? error?.message ?? translate('module.proxyManager.unknownError'),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionGuard
      resource={PROXY_MANAGER_PERMISSION_RESOURCE}
      action="update"
      fallback={<></>}
    >
      <MuiButton
        variant="outlined"
        color="info"
        size="small"
        disabled={!proxyId}
        startIcon={<Iconify icon="solar:pulse-bold" />}
        onClick={handleOpen}
        sx={{ minWidth: 160 }}
      >
        {translate('module.proxyManager.healthCheck')}
      </MuiButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('module.proxyManager.healthCheck')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {translate('module.proxyManager.healthCheckConfirm', { address })}
          </DialogContentText>
          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            size="small"
            options={defaultTargets}
            value={targets}
            onChange={(_event, value) =>
              setTargets(value.map((item) => item.trim()).filter(Boolean))
            }
            renderTags={(value, getTagProps) =>
              value.map((item, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={item}
                  label={item}
                  size="small"
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={translate('module.proxyManager.healthCheckTargets')}
                placeholder="https://example.com"
                helperText={translate('module.proxyManager.healthCheckTargetsHelper')}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton variant="outlined" onClick={() => setOpen(false)} disabled={loading}>
            {translate('actions.cancel')}
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || targets.length === 0}
          >
            {translate('actions.confirm')}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </PermissionGuard>
  );
};

export default HealthCheckButton;
